import { USER_ID_KEY, USER_TOKEN_KEY } from "../../../core/constants/app_constants";
import BasicResultModel from "../../domain/model/basic_result_model";
import ResultModel from "../../domain/model/result_model";
import ChatRepository from "../../domain/repository/chat_repository";
import { toChatModel } from "../../domain/mapper/init_chat_result_mapper";
import { toMessageModel, chatCompletionResultToMessageModel } from "../../domain/mapper/message_result_mapper";
import VideoCallDataModel from "../../domain/model/video_call_data_model";
import VoiceCallDataModel from "../../domain/model/voice_call_data_model";
import { VoiceCallStatus } from "../../domain/enum/voice_call_status";
import { toVoiceCallErrorModel } from "../../domain/mapper/voice_call_socket_error_mapper";
import { ErrorType } from "../../domain/enum/error_type";
import { MessagingResultType } from "../../domain/enum/messaging_result_type";
import MessagingInitDataResultModel from "../../domain/model/messaging_init_data_result_model";
import { VideoCallStatus } from "../../domain/enum/video_call_status";
import { toVideoCallErrorModel } from "../../domain/mapper/video_call_socket_error_mapper";
import { VideoCallRequestType } from "../../domain/enum/video_call_request_type";
import { toAIStatus } from "../../domain/enum/ai_status";
import { toAIMode } from "../../domain/enum/ai_mode";
import { MessageStatus } from "../../domain/enum/message_status";

class ChatRepositoryImpl extends ChatRepository {
  static #instance = null; // Private static instance

  constructor(chatApiService, localStorageService, videoCallSocketService, voiceCallSocketService, messagingSocketService) {
    if (ChatRepositoryImpl.#instance) {
      return ChatRepositoryImpl.#instance; // Return the same instance if already created
    }
    super(chatApiService, localStorageService, videoCallSocketService, voiceCallSocketService, messagingSocketService);
    this.audioStream = null;
    this.isCallEnded = false;
    ChatRepositoryImpl.#instance = this; // Store the instance
  }

  async getMessages({ chatChannelId, skip = 0, limit = 50 }) {
    const result = await this.chatApiService.getMessages({
      chatChannelId: chatChannelId,
      skip: skip,
      limit: limit,
    });
    if (result.success) {
      const messages = await Promise.all(result.data.map((message) => toMessageModel(message)));
      return new BasicResultModel(result.success, result.message, messages);
    } else {
      return new BasicResultModel(result.success, result.message);
    }
  }

  async sendMessage({ message, uniqueKey }) {
    this.messagingSocketService.sendMessage({ message, uniqueKey });
  }

  async getPreviousMessages({ chatChannelId, currentMessagesCount }) {
    const result = await this.getMessages({ chatChannelId, skip: currentMessagesCount });
    return result;
  }

  async startMessaging({
    resultCallback,
    errorCallback,
    noCreditsCallback,
    statusCallback,
    aiStatusCallback,
    aiResponseCallback,
    messageStatusCallback,
  }) {
    // Initialize Chat Channel
    const userId = await this.localStorageService.getData(USER_ID_KEY);
    const data = { userId };
    const result = await this.chatApiService.initChat(data);
    if (result.success) {
      const chatModel = toChatModel(result);

      // Get Initial Messages
      const chatChannelId = chatModel.id;
      const avatarId = chatModel.avatarId;
      const messagesResult = await this.getMessages({
        chatChannelId: chatChannelId,
      });
      if (messagesResult.success) {
        // Return the result to UI
        const resultModel = new MessagingInitDataResultModel({
          success: true,
          message: "Chat initialized successfully",
          chatData: chatModel.toJSON(),
          messagesData: messagesResult.data,
        });
        resultCallback(resultModel);

        // Connect to Messaging Socket
        this.messagingSocketService.connect({
          userId: userId,
          chatChannelId: chatChannelId,
          avatarId: avatarId,
          aiStatusCallback: (status) => {
            const aiStatus = toAIStatus(status);
            aiStatusCallback(aiStatus);
          },
          aiResponseCallback: async (response) => {
            const responseData = await chatCompletionResultToMessageModel(response[0]);
            aiResponseCallback(responseData);
          },
          errorCallback: (error) => {
            if (error.type === ErrorType.USR_NO_MSG_CREDITS) {
              noCreditsCallback();
            } else {
              /// Get the message from the error
              errorCallback(error.error[0].error);
            }
          },
          messageStatusCallback: (uniqueKey, status) => {
            var messageStatus = MessageStatus.sending;
            if (status === "Sent") {
              messageStatus = MessageStatus.sent;
            } else if (status === "Seen") {
              messageStatus = MessageStatus.seen;
            }
            messageStatusCallback(uniqueKey, messageStatus);
          },
        });
      } else {
        const resultModel = new MessagingInitDataResultModel({ success: false, message: messagesResult.message });
        resultCallback(resultModel);
      }
    } else {
      const resultModel = new MessagingInitDataResultModel({ success: false, message: result.message });
      resultCallback(resultModel);
    }
  }

  async startVideoCall({
    chatChannelId,
    videoCallStatusCB,
    noCreditsCB,
    errorCB,
    videoCallOnGoing = () => {
      console.log("Video call is ongoing.");
    },
  }) {
    videoCallStatusCB(VideoCallStatus.initiating);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      console.log("User already permitted the video and audio.");
    } catch (e) {
      console.warn(`Video call permission error:`, e);
      // Handle specific permission errors
      if (e.name === "NotAllowedError" || e.name === "PermissionDeniedError") {
        errorCB({
          type: "PERMISSION_DENIED",
          message: "Camera and microphone access is required for video calls. Please allow access in your browser settings.",
        });
      } else if (e.name === "NotFoundError") {
        errorCB({
          type: "DEVICE_NOT_FOUND",
          message: "No camera or microphone found. Please check your devices and try again.",
        });
      } else {
        errorCB({
          type: "MEDIA_ERROR",
          message: "Failed to access camera and microphone. Please check your device settings.",
        });
      }
      videoCallStatusCB(VideoCallStatus.ended);
      return new BasicResultModel(false, "Failed to access media devices");
    }

    try {
      let token = await this.localStorageService.getData(USER_TOKEN_KEY);
      const userId = await this.localStorageService.getData(USER_ID_KEY);

      this.videoCallSocketService.connect({
        userId: userId,
        chatChannelId,
        token,
        onConnected: () => {
          videoCallStatusCB(VideoCallStatus.calling);
        },
        onUpdate: (status, data) => {
          videoCallStatusCB(status, data);
        },
        onComplete: () => {
          videoCallStatusCB(VideoCallStatus.ended);
        },
        onDisconnected: () => {
          videoCallStatusCB(VideoCallStatus.ended);
        },
        onError: (error) => {
          console.log("Video call error:", error);
          if (error.type === ErrorType.USR_NO_VCC_CREDITS) {
            noCreditsCB(error);
          } else {
            errorCB(error);
          }
          this.videoCallSocketService.disconnect();
        },
      });
    } catch (error) {
      console.error("Failed to establish video call connection:", error);
      errorCB({
        type: "CONNECTION_ERROR",
        message: "Failed to establish video call connection. Please try again.",
      });
      videoCallStatusCB(VideoCallStatus.ended);
    }
  }

  async endVideoCall(chatChannelId) {
    try {
      const userId = await this.localStorageService.getData(USER_ID_KEY);

      // this.videoCallSocketService.emit('request', { type: VideoCallRequestType.endVideoCall, data: { chatChannelId: chatChannelId, userId } });
      this.videoCallSocketService.disconnect();
      // const data = {
      //     chatChannelId: chatChannelId,
      // }
      // await this.chatApiService.endVideoCall(data);
    } catch (error) {
      console.error("ChatRepositoryImpl endVideoCall error:", error);
    }
  }

  async startVoiceCall({ chatChannelId, callStatusCallback, noCreditsCallback, aiModeCallback, errorCallback, user = {} }) {
    this.isCallEnded = false;

    callStatusCallback(VoiceCallStatus.initiating);

    try {
      this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      console.log("Voice Call Permission Error", error);
      return new BasicResultModel(false, "Voice call permission denied");
    }

    if (this.isCallEnded) {
      this.closeMic();
      return new BasicResultModel(false, "Voice call has been ended");
    }

    callStatusCallback(VoiceCallStatus.calling);
    const userId = await this.localStorageService.getData(USER_ID_KEY);

    this.voiceCallSocketService.connect({
      userId: userId,
      chatChannelId: chatChannelId,
      userName: user.name,
      onConnected: () => {
        if (!this.isCallEnded) {
          callStatusCallback(VoiceCallStatus.ongoing);
        } else {
          this.voiceCallSocketService.disconnect();
          this.closeMic();
          callStatusCallback(VoiceCallStatus.ended);
        }
      },
      onComplete: () => {
        this.closeMic();
        if (!this.isCallEnded) {
          callStatusCallback(VoiceCallStatus.ended);
        }
      },
      onModeChange: (mode) => {
        if (!this.isCallEnded) {
          const aiMode = toAIMode(mode);
          aiModeCallback(aiMode);
        } else {
          this.voiceCallSocketService.disconnect();
          this.closeMic();
          callStatusCallback(VoiceCallStatus.ended);
        }
      },
      onError: (error) => {
        if (!this.isCallEnded) {
          if (error.type === ErrorType.USR_NO_VC_CREDITS) {
            noCreditsCallback();
          } else {
            const errorModel = toVoiceCallErrorModel(error).toJSON();
            errorCallback(errorModel);
          }
        } else {
          this.voiceCallSocketService.disconnect();
          this.closeMic();
          callStatusCallback(VoiceCallStatus.ended);
        }
      },
    });

    return new BasicResultModel(true, "Voice call started");
  }

  async endVoiceCall() {
    try {
      this.isCallEnded = true;
      this.closeMic();
      this.voiceCallSocketService.disconnect();
    } catch (error) {
      console.error("ChatRepositoryImpl endVoiceCall error:", error.message);
    }
  }

  async closeMic() {
    try {
      console.log("Audio Stream", this.audioStream);
      if (this.audioStream) {
        this.audioStream.getTracks().forEach((track) => track.stop());
        this.audioStream = null;
      }
    } catch (error) {
      console.error("ChatRepositoryImpl endVoiceCall error:", error.message);
    }
  }

  async clearChatMessages({ chatChannelId, resultCallback, errorCallback }) {
    try {
      const result = await this.chatApiService.clearChat({ chatChannelId });
      if (result.success) {
        resultCallback(result);
      } else {
        errorCallback({ message: result.message });
      }
    } catch (error) {
      errorCallback(error);
    }
  }
}

export default ChatRepositoryImpl;
